/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react';
import type { SelectItem } from '@mantine/core';
import { Select, createStyles } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';

import type { CellComponentProps, CellDescriptor } from '../../types/common';
import useAlignStyle from '../../../TableModal/useAlignStyle';
import { getDependenciesValues } from '../../../../../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../../../../../context/utils/getIsDependenciesSatisfied';

import type { TableCellDataSelectType, TableColumnSelectType } from './types';

const useStyles = createStyles(() => ({
  cellWrapper: {
    display: 'flex',
  },
  checkboxLabel: {
    whiteSpace: 'nowrap',
  },
}));

function TableCellSelect({
  column,
  row,
  cell,
  value,
  allValues,
  sectionKey,
  onChange,
  allErrors,
}: CellComponentProps<TableColumnSelectType, TableCellDataSelectType>) {
  const { classes, cx } = useStyles();
  const { classes: alignClasses } = useAlignStyle();

  const filteredOptions = useMemo(
    () =>
      cell.options.filter((option) => {
        if (!option.dependsOn) {
          return true;
        }

        const dependsOn = option.dependsOn;
        const dependencyValues = getDependenciesValues(allValues, sectionKey, dependsOn);
        const isDependencySatisfied = getIsDependenciesSatisfied(
          row.key,
          dependsOn,
          allErrors,
          dependencyValues
        );

        return isDependencySatisfied;
      }),
    [JSON.stringify(allErrors), JSON.stringify(allValues), cell.options, row.key, sectionKey]
  );

  useEffect(() => {
    if (typeof cell.defaultValue === 'undefined') {
      return;
    }
    if (typeof value !== 'undefined') {
      const isInOptions = filteredOptions.some((option) => option.value === value);
      if (!isInOptions) {
        onChange(filteredOptions[0]?.value ?? cell.defaultValue, true);
      }
      return;
    }
    if (filteredOptions.length === 0) {
      return;
    }

    onChange(cell.defaultValue, true);
  }, [JSON.stringify(filteredOptions), cell.defaultValue, onChange, value]);

  return (
    <div
      className={cx(classes.cellWrapper, {
        [alignClasses.start]: column.params?.align === 'start',
        [alignClasses.center]: column.params?.align === 'center',
        [alignClasses.end]: column.params?.align === 'end',
      })}
    >
      <Tooltip
        isRendered={!!cell.params?.tooltip}
        withinPortal
        position="top-start"
        key={`${row.key}${column.key}`}
        label={cell.params?.tooltip}
      >
        <Select
          data={filteredOptions as SelectItem[]}
          value={value as string}
          onChange={onChange}
          wrapperProps={{ 'data-test-id': dt.table.body.select }}
          disabled={cell.isDisabled}
        />
      </Tooltip>
    </div>
  );
}

const descriptor: CellDescriptor<TableColumnSelectType, TableCellDataSelectType> = {
  name: 'Select',
  Cell: TableCellSelect,
  createValidationSchema(cell) {
    let schema: ZodTypeAny = z.union([z.string(), z.number(), z.boolean()]);
    if (cell.validation?.isOptional) {
      schema = schema.optional();
    }

    return {
      schema,
    };
  },
};

export default descriptor;
